import { useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useAppDispatch, useTrading } from "features/store";
import {
  setCurrentStrategy,
  setCurrentStrategyGroup,
} from "features/trading/tradingSlice";
import { useListStrategiesQuery } from "features/trading/accounts";

export const useUrlStrategy = () => {
  const dispatch = useAppDispatch();
  const { currentStrategy: storeCurrentStrategy } = useTrading();
  const [searchParams] = useSearchParams();

  const strategyGroupIdParam = searchParams.get("strategy_group_id");
  const strategyIdParam = searchParams.get("strategy_id");

  const hasUrlParams = useMemo(
    () => strategyIdParam && strategyGroupIdParam,
    [strategyIdParam, strategyGroupIdParam],
  );

  const strategyId = useMemo(
    () => (strategyIdParam ? parseInt(strategyIdParam, 10) : null),
    [strategyIdParam],
  );

  const strategyGroupId = useMemo(
    () => (strategyGroupIdParam ? parseInt(strategyGroupIdParam, 10) : null),
    [strategyGroupIdParam],
  );

  const {
    data: urlStrategyData,
    isLoading: isUrlStrategyLoading,
    isFetching: isUrlStrategyFetching,
  } = useListStrategiesQuery(
    {
      pagination: { page: 0, pageSize: 100 },
      filters: {
        id: strategyId,
        strategy_group: strategyGroupId,
        include_last_call_close: true,
      },
    },
    { skip: !hasUrlParams || !strategyId || !strategyGroupId },
  );

  const urlStrategy = useMemo(() => {
    if (!hasUrlParams || !urlStrategyData?.results) return null;
    return urlStrategyData.results.find((s) => s.id === strategyId) || null;
  }, [hasUrlParams, urlStrategyData, strategyId]);

  useEffect(() => {
    if (hasUrlParams && urlStrategy && strategyGroupId) {
      dispatch(setCurrentStrategyGroup(strategyGroupId));
      dispatch(setCurrentStrategy(urlStrategy));
    }
  }, [hasUrlParams, urlStrategy, strategyGroupId, dispatch]);

  const currentStrategy = useMemo(() => {
    return hasUrlParams && urlStrategy ? urlStrategy : storeCurrentStrategy;
  }, [hasUrlParams, urlStrategy, storeCurrentStrategy]);

  const isStrategyLoaded = useMemo(() => {
    // If there are no URL params, strategy is always "loaded" (using store strategy)
    if (!hasUrlParams) return true;
    // If URL params exist, wait for strategy to be loaded
    return !isUrlStrategyLoading && !!urlStrategy;
  }, [hasUrlParams, isUrlStrategyLoading, urlStrategy]);

  return {
    currentStrategy,
    isStrategyLoaded,
    hasUrlParams,
    urlStrategy,
    isUrlStrategyLoading,
    isUrlStrategyFetching,
  };
};
